import { esUsuarioInstitucional } from 'src/app/access-control/utils/usuario-institucional.util';
import { MenuItem } from '../interface/menu-item.interface';
/**
 * Genera un menú dinámico basado en el rol del usuario y sus permisos
 *
 * Esta función construye un array de items de menú que se adapta según el rol del usuario
 * y si tiene permisos de comercializador. El menú resultante se utiliza para mostrar
 * las opciones de navegación disponibles para cada tipo de usuario.
 *
 * @param rolUsuario - Rol del usuario actual (ej: 'AgenteVigilado', 'Administrador (UPRA)')
 * @param opcionesComercializadores - Indica si el usuario tiene permisos de comercializador
 * @param enPaginaCargarEmpresas - Indica si el usuario está en la página de selección de empresas
 * @param tieneEmpresaAprobada - Indica si el usuario tiene al menos una empresa aprobada
 * @param estadoClasificacion - Estado de clasificación del comercializador
 * @param userEmail - Correo del usuario para validar acceso institucional (UPRA/MADR)
 * @returns Array de MenuItem con la estructura del menú correspondiente
 *
 * @example
 * // Para un Agente Vigilado sin opciones de comercializador
 * const menu = generarMenu('AgenteVigilado', false, false);
 *
 * @example
 * // Para un Administrador UPRA
 * const menu = generarMenu('Administrador (UPRA)', false, false);
 *
 * @example
 * // Para un usuario con permisos de comercializador
 * const menu = generarMenu('AgenteVigilado', true, false);
 *
 * @since 1.0.0
 */

export function generarMenu(
    rolUsuario: string,
    opcionesComercializadores: boolean,
    enPaginaCargarEmpresas = false,
    tieneEmpresaAprobada = true,
    estadoClasificacion?: string,
    userEmail: string | null = null
): MenuItem[] {
    const esInstitucional = esUsuarioInstitucional(userEmail);

    // Si está en la página de cargar empresas, retornar menú vacío o mínimo
    if (enPaginaCargarEmpresas) {
        return [];
    }

    const menu: MenuItem[] = [
        {
            label: 'Inicio',
            route: 'inicio',
            paddingLeft: '1.6rem',
            active: true,
        },
    ];

    if (!esInstitucional) {
        // Si el usuario no tiene empresa aprobada, mostrar solo opción de crear/asociar
        if (rolUsuario === 'AgenteVigilado' && !tieneEmpresaAprobada) {
            menu.push(
                { label: 'Administrar', header: true },
                {
                    label: 'Crear o Asociar',
                    route: 'nueva-empresa',
                    iconClass: 'fas fa-building',
                }
            );
            menu.push({ label: 'Ayuda', route: 'inicio', icon: 'ayuda.png' });
            return menu;
        }

        // Menú para Agente Vigilado
        if (rolUsuario === 'AgenteVigilado' && !opcionesComercializadores) {
            menu.push(
                { label: 'Productos', header: true },
                { label: 'Productos', route: 'consultar', icon: 'buscar.png' },
                {
                    label: 'Reportes',
                    route: 'carga-consulta-reportes',
                    icon: 'crear.png',
                },
                {
                    label: 'Novedades',
                    route: 'carga-consulta-novedades',
                    iconClass: 'fas fa-file-signature',
                },
                { label: 'Administrar', header: true },
                {
                    label: 'Crear o Asociar',
                    route: 'nueva-empresa',
                    iconClass: 'fas fa-building',
                }
            );
        }

        // Menú para Comercializadores
        if (rolUsuario === 'AgenteVigilado' && opcionesComercializadores) {
            menu.push({
                label: 'Productos',
                route: 'consultar-productos-comercializadores',
                icon: 'crear.png',
            });

            if (estadoClasificacion === 'clasificada') {
                menu.push({
                    label: 'Reportes',
                    route: 'reportes-ventas-comercializador',
                    icon: 'crear.png',
                });
            }

            menu.push(
                { label: 'Administrar', header: true },
                {
                    label: 'Crear o Asociar',
                    route: 'nueva-empresa',
                    iconClass: 'fas fa-building',
                }
            );
        }
    }

    // Menú para Administrador (UPRA) o usuarios institucionales (UPRA/MADR)
    if (rolUsuario === 'Administrador (UPRA)' || esInstitucional) {
        menu.push(
            {
                label: 'Usuarios',
                header: true,
                permissions: [
                    'consultar_usuarios_empresa',
                    'consultar_usuarios_upra',
                    'consultar_empresas_admin_empresa',
                    'realizar_seguimiento_de_cumplimiento_empresas_productor',
                    'realizar_seguimiento_de_cumplimiento_empresas_comercializador',
                ],
            },
            {
                label: 'Usuarios',
                route: 'busqueda-user',
                iconClass: 'fas fa-user',
                permissions: [
                    'consultar_usuarios_empresa',
                    'consultar_usuarios_upra',
                ],
            },
            {
                label: 'Empresas',
                route: 'busqueda-agente-vigilado',
                iconClass: 'fas fa-building',
                permissions: ['consultar_empresas_admin_empresa'],
            },
            {
                label: 'Cumplimiento',
                route: 'reporte-cumplimiento-productor',
                iconClass: 'fas fa-server',
                permissions: [
                    'realizar_seguimiento_de_cumplimiento_empresas_productor',
                    'realizar_seguimiento_de_cumplimiento_empresas_comercializador',
                ],
            },
            {
                label: 'Gestión',
                header: true,
                permissions: [
                    'editar_productos_masivamente',
                ],
            },
            {
                label: 'Edición masiva productos',
                route: 'edita-masiva-productos',
                iconClass: 'fas fa-edit',
                permissions: ['editar_productos_masivamente'],
            },
            {
                label: 'Comercializador',
                header: true,
                permissions: ['clasificacion_comercializadores'],
            },
            {
                label: 'Clasificación',
                route: 'clasificacion-comercializador',
                iconClass: 'fas fa-list',
                permissions: ['clasificacion_comercializadores'],
            },
            {
                label: 'Parámetros',
                header: true,
                permissions: ['consultar_parametros'],
            },
            {
                label: 'Parámetros',
                route: 'parametros',
                iconClass: 'fas fa-wrench',
                permissions: ['consultar_parametros'],
            },
            {
                label: 'Reportes',
                header: true,
                permissions: [
                    'consultar_tableros',
                    'consultar_reportes_madr',
                    'consultar_reportes_a_cargar_madr',
                ],
            },
            {
                label: 'Cargar',
                route: 'carga-reportes-madr',
                iconClass: 'fas fa-file-upload',
                permissions: ['consultar_reportes_a_cargar_madr'],
            },
            {
                label: 'Consultar',
                route: 'consulta-reportes-madr',
                iconClass: 'fas fa-signal',
                permissions: ['consultar_reportes_madr'],
            },
            {
                label: 'Tableros BI',
                route: 'tableros-bi',
                iconClass: 'fas fa-signal',
                permissions: ['consultar_tableros'],
            }
        );
    }

    // Común para todos los roles
    menu.push({ label: 'Ayuda', route: 'inicio', icon: 'ayuda.png' });
    return menu;
}
